/**
 * ESC/POS command encoder — builds a byte buffer of printer commands.
 *
 * Chainable API: new EscPosEncoder(48).bold().text('Hello').cut().encode()
 * Zero external dependencies. Uses TextEncoder for UTF-8.
 */
import {
  INIT,
  ALIGN_LEFT, ALIGN_CENTER, ALIGN_RIGHT,
  BOLD_ON, BOLD_OFF,
  UNDERLINE_ON, UNDERLINE_OFF,
  SIZE_NORMAL, SIZE_DOUBLE_WIDTH, SIZE_DOUBLE_HEIGHT, SIZE_DOUBLE_BOTH,
  FEED_PREFIX, CUT_FULL, CUT_PARTIAL,
  CASH_DRAWER_KICK,
  QR_MODEL_2, QR_ERROR_M,
  BARCODE_CODE128, BARCODE_TEXT_BELOW, BARCODE_HEIGHT_PREFIX, BARCODE_WIDTH_PREFIX,
  GS, LF,
  CHARS_58MM, CHARS_80MM,
} from './constants';

type TextAlign = 'left' | 'center' | 'right';
type TextSize = 'normal' | 'double-width' | 'double-height' | 'double-both';

const textEncoder = new TextEncoder();

/**
 * ESC/POS thermal printers render a fixed single-byte code page — they have
 * no Unicode currency glyphs. Currency symbols that are not ASCII are mapped
 * to an ASCII-safe form before encoding so the printer never emits an
 * unprintable glyph:
 *
 *   ₹     → "Rs."   (INR — unchanged; keeps every INR byte stream identical)
 *   د.إ   → "AED"
 *   .د.ب  → "BHD"
 *   د.ك   → "KWD"
 *
 * SAR/QAR/OMR already resolve to their ISO 4217 code (already ASCII) in
 * `@bbpos/utils`, so they print correctly with no mapping here. Only the
 * currency glyphs are rewritten — all other text (business names, footers,
 * customer names, …) is still emitted as raw UTF-8, exactly as before, so
 * non-currency content is unaffected.
 */
const CURRENCY_GLYPH_TO_ASCII: ReadonlyArray<readonly [RegExp, string]> = [
  [/₹/g, 'Rs.'],
  [/د\.إ/g, 'AED'],
  [/\.د\.ب/g, 'BHD'],
  [/د\.ك/g, 'KWD'],
];

/**
 * Rewrite non-ASCII currency symbols to their printable ASCII form. Exported
 * so the receipt builder can measure column widths against the bytes that
 * will actually be printed — the encoder applies this same transform on
 * output, so callers stay aligned. Non-currency text is returned untouched.
 */
export function toPrintableAscii(str: string): string {
  let out = str;
  for (const [glyph, ascii] of CURRENCY_GLYPH_TO_ASCII) {
    out = out.replace(glyph, ascii);
  }
  return out;
}

/** Encode a string to bytes, sanitizing non-ASCII currency glyphs first. */
function encodeText(str: string): Uint8Array {
  return textEncoder.encode(toPrintableAscii(str));
}

export class EscPosEncoder {
  private chunks: Uint8Array[] = [];
  private readonly lineWidth: number;

  constructor(lineWidth?: number) {
    this.lineWidth = lineWidth ?? CHARS_80MM;
    this.chunks.push(INIT);
  }

  /** Create an encoder for a specific paper width. */
  static forPaper(paperMm: 58 | 80): EscPosEncoder {
    return new EscPosEncoder(paperMm === 58 ? CHARS_58MM : CHARS_80MM);
  }

  /** Get the character width of the current paper. */
  get charsPerLine(): number {
    return this.lineWidth;
  }

  // ─── Alignment ──────────────────────────────────────────────────────────

  align(alignment: TextAlign): this {
    const cmd = alignment === 'center' ? ALIGN_CENTER
      : alignment === 'right' ? ALIGN_RIGHT
        : ALIGN_LEFT;
    this.chunks.push(cmd);
    return this;
  }

  left(): this { return this.align('left'); }
  center(): this { return this.align('center'); }
  right(): this { return this.align('right'); }

  // ─── Text emphasis ────────────────────────────────────────────────────

  bold(on = true): this {
    this.chunks.push(on ? BOLD_ON : BOLD_OFF);
    return this;
  }

  underline(on = true): this {
    this.chunks.push(on ? UNDERLINE_ON : UNDERLINE_OFF);
    return this;
  }

  // ─── Text size ────────────────────────────────────────────────────────

  size(s: TextSize): this {
    const cmd = s === 'double-width' ? SIZE_DOUBLE_WIDTH
      : s === 'double-height' ? SIZE_DOUBLE_HEIGHT
        : s === 'double-both' ? SIZE_DOUBLE_BOTH
          : SIZE_NORMAL;
    this.chunks.push(cmd);
    return this;
  }

  normal(): this { return this.size('normal'); }

  // ─── Text output ──────────────────────────────────────────────────────

  /** Print text (no line feed). */
  text(str: string): this {
    this.chunks.push(encodeText(str));
    return this;
  }

  /** Print text followed by a line feed. */
  line(str: string): this {
    this.chunks.push(encodeText(str));
    this.chunks.push(new Uint8Array([LF]));
    return this;
  }

  /** Print an empty line. */
  newline(): this {
    this.chunks.push(new Uint8Array([LF]));
    return this;
  }

  /** Feed n lines. */
  feed(lines = 1): this {
    this.chunks.push(FEED_PREFIX);
    this.chunks.push(new Uint8Array([lines]));
    return this;
  }

  // ─── Layout helpers ───────────────────────────────────────────────────

  /** Print a horizontal separator line (dashes). */
  separator(char = '-'): this {
    return this.line(char.repeat(this.lineWidth));
  }

  /**
   * Print a two-column row: label left-aligned, value right-aligned.
   * Pads with spaces to fill the line width.
   */
  twoColumn(label: string, value: string): this {
    const gap = this.lineWidth - label.length - value.length;
    if (gap < 1) {
      // Content too long — print on two lines
      this.line(label);
      return this.right().line(value).left();
    }
    return this.line(label + ' '.repeat(gap) + value);
  }

  /**
   * Print a three-column row (e.g. Item | Qty | Amount).
   * col1 is left-aligned, col2 centered, col3 right-aligned.
   */
  threeColumn(col1: string, col2: string, col3: string): this {
    const totalUsed = col1.length + col2.length + col3.length;
    const remaining = this.lineWidth - totalUsed;
    if (remaining < 2) {
      return this.line(`${col1} ${col2} ${col3}`);
    }
    const gap1 = Math.floor(remaining / 2);
    const gap2 = remaining - gap1;
    return this.line(col1 + ' '.repeat(gap1) + col2 + ' '.repeat(gap2) + col3);
  }

  // ─── Paper cut ────────────────────────────────────────────────────────

  cut(partial = false): this {
    this.feed(3);
    this.chunks.push(partial ? CUT_PARTIAL : CUT_FULL);
    return this;
  }

  // ─── Cash drawer ──────────────────────────────────────────────────────

  cashDrawer(): this {
    this.chunks.push(CASH_DRAWER_KICK);
    return this;
  }

  // ─── QR Code ──────────────────────────────────────────────────────────

  /**
   * Print a QR code containing the given data string.
   * Uses GS ( k command set — model 2, error correction M, module size 4.
   */
  qrCode(data: string, moduleSize = 4): this {
    const bytes = encodeText(data);
    const len = bytes.length + 3;
    const pL = len & 0xff;
    const pH = (len >> 8) & 0xff;

    // Set model
    this.chunks.push(QR_MODEL_2);
    // Set module size
    this.chunks.push(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, moduleSize]));
    // Set error correction
    this.chunks.push(QR_ERROR_M);
    // Store data
    this.chunks.push(new Uint8Array([GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30]));
    this.chunks.push(bytes);
    // Print stored QR code
    this.chunks.push(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]));

    return this;
  }

  // ─── Barcode (Code128) ────────────────────────────────────────────────

  /**
   * Print a Code128 barcode with text below.
   */
  barcode(data: string, height = 50, width = 2): this {
    // Set barcode text position below
    this.chunks.push(BARCODE_TEXT_BELOW);
    // Set height
    this.chunks.push(BARCODE_HEIGHT_PREFIX);
    this.chunks.push(new Uint8Array([height]));
    // Set width
    this.chunks.push(BARCODE_WIDTH_PREFIX);
    this.chunks.push(new Uint8Array([width]));
    // Print Code128 barcode
    const dataBytes = encodeText(data);
    this.chunks.push(new Uint8Array([GS, 0x6b, BARCODE_CODE128, dataBytes.length]));
    this.chunks.push(dataBytes);

    return this;
  }

  // ─── Raw bytes ────────────────────────────────────────────────────────

  raw(bytes: Uint8Array): this {
    this.chunks.push(bytes);
    return this;
  }

  // ─── Encode ───────────────────────────────────────────────────────────

  /** Concatenate all buffered commands into a single Uint8Array. */
  encode(): Uint8Array {
    let totalLength = 0;
    for (const chunk of this.chunks) {
      totalLength += chunk.length;
    }
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of this.chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }
}
